import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { Button, Grid, TextField, FormControlLabel, Checkbox, Typography, Box } from "@mui/material";
import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import BaseSelect from "components/Shared/Base/BaseSelect";
import AppSwitch from "components/Shared/AppSwitch";
import { useLazyGetDDLBranchListQuery } from "store/services/branchApi";
import { useListListQuery } from "store/services/listService";
import { ENTITY_LIST_CONSTANTS } from "data/static";
import { useAuth } from "hooks/useAuth";

const INITIAL_STATE = {
  branch: [],
  toBranch: [],
  transportationMode: "",
  maxDistanceKm: 5,
  maxCost: "",
  isActive: "true",
  description: "",
  pricePerSack: 0,
  isBidirectional: false,
};

const LocalTransportRuleForm = ({
  isBusy = false,
  ruleDetail = null,
  onSubmit = () => { },
}) => {

  const navigate = useNavigate();
  const { viewEntity } = useAuth();
  const [isToBranchEnabled, setIsToBranchEnabled] = useState(false);

  const [fetchBranches, { data: branches, isLoading: loadingBranches }] =
    useLazyGetDDLBranchListQuery();

  const { data: modeList = [], isLoading: loadingModes } = useListListQuery(
    {
      type: ENTITY_LIST_CONSTANTS.TRANSPORT_MODE,
      status: "Active",
    },
    { refetchOnMountOrArgChange: true }
  );

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: INITIAL_STATE,
  });

  // Fetch branches
  React.useEffect(() => {
    if (viewEntity?.entity?._id) {
      fetchBranches({
        entity: viewEntity.entity._id,
        account: viewEntity.entity.account._id,
      });
    }
  }, [
    viewEntity?.entity?._id,
    viewEntity?.entity?.account?._id,
    fetchBranches,
  ]);

  // Populate on edit
  React.useEffect(() => {
    if (ruleDetail) {
      setValue("branch", ruleDetail.branch || []);
      setValue("toBranch", ruleDetail.toBranch || []);
      setValue("transportationMode", ruleDetail.transportationMode?._id || "");
      setValue("maxDistanceKm", ruleDetail.maxDistanceKm);
      setValue("maxCost", ruleDetail.maxCost || "");
      setValue("isActive", ruleDetail.isActive ? "true" : "false");
      setValue("isBidirectional", !!ruleDetail.isBidirectional);
      setValue("description", ruleDetail.description || "");
      setValue("pricePerSack", ruleDetail.pricePerSack || 0);


      // Enable toBranch checkbox if toBranch exists
      if (ruleDetail.toBranch && ruleDetail.toBranch.length > 0) {
        setIsToBranchEnabled(true);
      } else {
        // If not branch-to-branch, ensure maxDistanceKm is set to 5
        setValue("maxDistanceKm", ruleDetail.maxDistanceKm || 5);
      }
    }
  }, [ruleDetail, setValue]);

  // Memoized modes
  const _mappedModes = useMemo(
    () =>
      Array.isArray(modeList)
        ? modeList.map((item) => ({
          label: item?.name || "",
          value: item?._id || "",
        }))
        : [],
    [modeList]
  );

  // Handle checkbox change
  const handleCheckboxChange = (event) => {
    const isChecked = event.target.checked;
    setIsToBranchEnabled(isChecked);

    if (isChecked) {
      // When branch-to-branch is enabled, set maxDistanceKm to 0
      setValue("maxDistanceKm", 0);
    } else {
      // When branch-to-branch is disabled, set maxDistanceKm to 5 and clear toBranch
      setValue("maxDistanceKm", 5);
      setValue("toBranch", []);
      setValue("pricePerSack", 0);
    }
  };

  // Filter branches for toBranch (exclude already selected branches)
  const filteredToBranches = useMemo(() => {
    if (!branches || !Array.isArray(branches)) return [];

    const selectedBranchIds = control._formValues.branch?.map(b => b._id) || [];

    return branches.filter(branch =>
      !selectedBranchIds.includes(branch._id)
    );
  }, [branches, control._formValues.branch]);

  const handleOnSubmit = (data) => {
    const payload = {
      ...data,
      entity: viewEntity?.entity?._id,
      branch: data.branch?.map((b) => b._id) || [],
      toBranch: isToBranchEnabled && data.toBranch ? data.toBranch?.map((b) => b._id) || [] : [],
      maxDistanceKm: Number(data.maxDistanceKm),
      maxCost: data.maxCost ? Number(data.maxCost) : undefined,
      pricePerSack: isToBranchEnabled ? Number(data.pricePerSack) : undefined,
      isActive: data.isActive === "true",
    };
    onSubmit(payload);
  };

  return (
    <AppForm loading={isBusy || loadingBranches || loadingModes}>
      <AppForm.Content>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box sx={{
              p: 2,
              borderRadius: 1,
              borderLeft: '4px solid #1976d2',
            }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                <strong>Branch-to-Branch Transport</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Enable this option if this rule is specifically for transporting goods between different branches.
                When enabled, you can set a <strong>price per sack</strong> instead of a general max cost.
              </Typography>
            </Box>
          </Grid>
          {/* To Branch Checkbox + Bidirectional (same line) */}
          <Grid container item xs={12} alignItems="center">
            <Grid item xs={6} sm={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isToBranchEnabled}
                    onChange={handleCheckboxChange}
                  />
                }
                label="This rule applies to branch-to-branch transport"
              />
            </Grid>

            {/* Is Bidirectional - ONLY when Branch-to-Branch */}
            {isToBranchEnabled && (
              <Grid item xs={6} sm={9}>
                <Controller
                  name="isBidirectional"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={!!field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label="Apply this rule bidirectionally (A ↔ B)"
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>

          {/* From Branch - Multiple */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="branch"
              control={control}
              rules={{ required: "At least one branch is required" }}
              render={({ field }) => (
                <BaseAutocomplete
                  {...field}
                  multiple
                  fullWidth
                  label="From Branch"
                  required
                  value={field.value || []}
                  onChange={(_, data) => field.onChange(data)}
                  getOptionLabel={(o) => o?.name || ""}
                  isOptionEqualToValue={(o, v) => o?._id === v?._id}
                  options={branches || []}
                  loading={loadingBranches}
                  loadingText="Loading Branches..."
                  error={!!errors.branch}
                  helperText={errors.branch?.message}
                />
              )}
            />
          </Grid>

          {/* To Branch - Conditional */}
          {isToBranchEnabled && (
            <Grid item xs={12} sm={6}>
              <Controller
                name="toBranch"
                control={control}
                rules={isToBranchEnabled ? { required: "At least one destination branch is required" } : {}}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    multiple
                    fullWidth
                    label="To Branch"
                    required={isToBranchEnabled}
                    value={field.value || []}
                    onChange={(_, data) => field.onChange(data)}
                    getOptionLabel={(o) => o?.name || ""}
                    isOptionEqualToValue={(o, v) => o?._id === v?._id}
                    options={filteredToBranches}
                    loading={loadingBranches}
                    loadingText="Loading Branches..."
                    error={!!errors.toBranch}
                    helperText={errors.toBranch?.message}
                  />
                )}
              />
            </Grid>
          )}

          {/* Transportation Mode */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="transportationMode"
              control={control}
              rules={{ required: "Mode is required" }}
              render={({ field }) => (
                <BaseSelect
                  fullWidth
                  required
                  label="Transportation Mode"
                  options={_mappedModes}
                  getOptionLabel={(opt) => opt.label}
                  getOptionValue={(opt) => opt.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  value={field.value}
                  error={!!errors.transportationMode}
                  helperText={errors.transportationMode?.message}
                  loading={loadingModes}
                  loadingText="Loading Modes..."
                />
              )}
            />
          </Grid>

          {/* Max Distance */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="maxDistanceKm"
              control={control}
              rules={isToBranchEnabled ? {
                min: { value: 0, message: "Min 0 km" }
              } : {
                required: "Max distance is required",
                min: { value: 0.1, message: "Min 0.1 km" }
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required={!isToBranchEnabled}
                  fullWidth
                  label="Max Distance (km)"
                  type="number"
                  inputProps={{ step: "0 ", min: "0.1" }}
                  size="small"
                  error={!!errors.maxDistanceKm}
                  helperText={errors.maxDistanceKm?.message}
                />
              )}
            />
          </Grid>

          {/* Max Cost */}
          {!isToBranchEnabled && (
            <Grid item xs={12} sm={6}>
              <Controller
                name="maxCost"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Max Cost (Optional)"
                    type="number"
                    inputProps={{ step: "0.01" }}
                    size="small"
                  />
                )}
              />
            </Grid>
          )}

          {/* Price Per Sack - Conditional */}
          {isToBranchEnabled && (
            <Grid item xs={12} sm={6}>
              <Controller
                name="pricePerSack"
                control={control}
                rules={isToBranchEnabled ? {
                  min: { value: 0, message: "Price cannot be negative" }
                } : {}}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    required
                    label="Price Per Sack"
                    type="number"
                    inputProps={{ step: "0.01", min: "0" }}
                    size="small"
                    error={!!errors.pricePerSack}
                    helperText={errors.pricePerSack?.message}
                  />
                )}
              />
            </Grid>
          )}

          {/* Is Active */}
          {ruleDetail && (
            <Grid item xs={6}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <AppSwitch {...field} label="Is Active?" />
                )}
              />
            </Grid>
          )}

          {/* Description */}
          <Grid item xs={12}>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Description"
                  multiline
                  rows={2}
                  size="small"
                />
              )}
            />
          </Grid>
        </Grid>
      </AppForm.Content>

      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit(handleOnSubmit)}>
          {ruleDetail ? "Update" : "Create"} Rule
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default LocalTransportRuleForm;